import { useEffect, useRef, useState } from 'react';
import aboutStaffImage from '../../../assets/images/about-staff.jpg';
import { WHATSAPP_CHAT_URL } from '../../../config/contactLinks';

const bigStats = [
  { value: 500, suffix: '+', label: 'Tamamlanan Proje', icon: 'ri-check-double-line' },
  { value: 5, suffix: '+', label: 'Yıllık Deneyim', icon: 'ri-calendar-check-line' },
  { value: 11, suffix: '', label: 'Hizmet Çeşidi', icon: 'ri-service-line' },
  { value: 100, prefix: '%', suffix: '', label: 'Memnuniyet', icon: 'ri-emotion-happy-line' },
];

function useCountUp(target: number, duration = 1800, started: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return count;
}

function StatCard({ stat, index, started }: {
  stat: typeof bigStats[0];
  index: number;
  started: boolean;
}) {
  const count = useCountUp(stat.value, 1800, started);

  return (
    <div
      key={stat.label}
      className="py-10 flex flex-col items-center gap-2 group px-4 transition-all"
      style={{
        opacity: started ? 1 : 0,
        transform: started ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${index * 0.12}s, transform 0.6s ease ${index * 0.12}s`,
      }}
    >
      <span
        className="w-10 h-10 flex items-center justify-center bg-gold/15 rounded-xl mb-1 group-hover:bg-gold/25 transition-colors"
        style={{
          transform: started ? 'scale(1)' : 'scale(0.5)',
          transition: `transform 0.5s cubic-bezier(0.34,1.56,0.64,1) ${index * 0.12 + 0.2}s`,
        }}
      >
        <i className={`${stat.icon} text-gold text-lg`}></i>
      </span>
      <div className="text-4xl md:text-5xl font-extrabold text-white leading-none tabular-nums">
        {stat.prefix ?? ''}{count}{stat.suffix}
      </div>
      <div className="text-white/40 text-[11px] font-semibold uppercase tracking-[0.15em] text-center">{stat.label}</div>
    </div>
  );
}

export default function About() {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsStarted, setStatsStarted] = useState(false);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="hakkimizda" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Two column */}
        <div className="flex flex-col lg:flex-row gap-14 lg:gap-16 items-center mb-20">

          {/* Left — Image with layered design */}
          <div className="w-full lg:w-[46%] shrink-0">
            <div className="relative">
              <div className="absolute top-6 left-6 right-0 bottom-0 bg-navy-light rounded-2xl -z-0" />
              <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                <img
                  src={aboutStaffImage}
                  alt="NiğPak Temizlik ekibi"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-navy/20 to-transparent" />
              </div>
              <div className="absolute -bottom-3 -right-3 w-24 h-24 border-4 border-gold rounded-xl -z-0" />
              <div className="absolute -bottom-5 left-8 bg-navy text-white rounded-xl px-5 py-4 z-10">
                <div className="flex items-end gap-1">
                  <span className="text-3xl font-extrabold text-gold leading-none">10</span>
                  <span className="text-gold font-extrabold text-lg leading-snug">+</span>
                </div>
                <div className="text-[10px] font-bold mt-1 text-white/60 uppercase tracking-wider">Yıllık Deneyim</div>
              </div>
              <div className="absolute top-5 -right-4 bg-white rounded-xl px-4 py-3 z-10"
                style={{ boxShadow: '0 4px 20px rgba(26,43,94,0.12)' }}>
                <div className="flex items-center gap-1 mb-1">
                  {[1,2,3,4,5].map(i => (
                    <span key={i} className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-gold text-xs"></i>
                    </span>
                  ))}
                </div>
                <p className="text-navy font-extrabold text-xs">500+ Yorum</p>
                <p className="text-gray-400 text-[10px]">Memnun Müşteri</p>
              </div>
            </div>
          </div>

          {/* Right — Content */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-[2px] bg-gold rounded-full" />
              <p className="text-gold text-[11px] font-bold uppercase tracking-[0.25em]">Biz Kimiz?</p>
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-navy tracking-tight leading-tight mb-3">
              NiğPak Temizlik
            </h2>
            <p className="text-navy/40 text-[11px] font-bold uppercase tracking-[0.22em] mb-5">
              Niğde&apos;nin Temizlik Uzmanları
            </p>
            <div className="w-10 h-[2px] bg-gold mb-7" />
            <p className="text-gray-500 text-sm leading-[1.9] mb-4">
              <strong className="text-navy font-semibold">NiğPak Temizlik</strong>, Niğde Merkez&apos;de kurulmuş, 5 yılı aşkın deneyimiyle bölgenin en güvenilir profesyonel temizlik firmasıdır. Müşterilerimize mükemmel temizlik hizmeti sunmak için her gün titizlikle çalışıyoruz.
            </p>
            <p className="text-gray-500 text-sm leading-[1.9] mb-10">
              Daire temizliğinden villa temizliğine, inşaat sonrasından kurumsal mağazaya kadar 11 farklı temizlik hizmetiyle Niğde&apos;deki her ihtiyacınıza çözüm üretiyoruz.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {[
                { icon: 'ri-shield-check-line', title: 'Sigortalı Ekip', desc: 'Tüm personelimiz sigortalı ve güvenlik kontrolünden geçmiş.' },
                { icon: 'ri-leaf-line', title: 'Çevre Dostu', desc: 'Sertifikalı, doğa ve insan sağlığına uygun ürünler.' },
                { icon: 'ri-time-line', title: 'Zamanında Hizmet', desc: 'Belirtilen saat ve günde, hiç geç kalmadan hizmet.' },
                { icon: 'ri-tools-line', title: 'Profesyonel Ekipman', desc: 'Endüstriyel makineler ile üstün temizlik kalitesi.' },
              ].map((f) => (
                <div key={f.title}
                  className="flex items-start gap-3 border border-gray-100 rounded-xl p-4 hover:border-gold/40 transition-colors"
                  style={{ boxShadow: '0 1px 4px rgba(26,43,94,0.05)' }}>
                  <span className="w-9 h-9 flex items-center justify-center bg-gold/10 rounded-lg shrink-0">
                    <i className={`${f.icon} text-gold text-base`}></i>
                  </span>
                  <div>
                    <p className="text-navy font-bold text-xs mb-0.5">{f.title}</p>
                    <p className="text-gray-400 text-xs leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <a href={WHATSAPP_CHAT_URL} target="_blank" rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 bg-navy hover:bg-gold text-white font-bold px-6 py-3.5 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line text-base"></i>
              Ücretsiz Teklif Al
            </a>
          </div>
        </div>

        {/* Stats row */}
        <div ref={statsRef} className="relative rounded-2xl overflow-hidden bg-navy">
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'radial-gradient(circle, #c9a84c 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
          <div className="relative grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {bigStats.map((s, i) => (
              <StatCard key={s.label} stat={s} index={i} started={statsStarted} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
